import type { AuthState } from "./auth";
import { safeReturnTo } from "./auth";

export const ACCOUNT_LOGIN_PATH = "/account/login";
export const ACCOUNT_HOME_PATH = "/account";

const guestOnlyPaths = new Set([
  "/account/login",
  "/account/register",
  "/account/forgot-password",
  "/account/forgot-password/sent",
  "/account/reset-password",
]);

const publicAccountPaths = new Set([
  ...guestOnlyPaths,
  "/account/wishlist",
  "/account/verify-email",
  "/account/verify-email/sent",
  "/account/success",
  "/account/confirm-email-change",
]);

export function legacyAccountRedirect(route: RouteLike): string | null {
  const path = String(route.path || "");
  const exact: Record<string, string> = {
    "/account/edit": "/account/profile",
    "/account/password": "/account/security",
    "/account/address": "/account/addresses",
    "/account/address/create": "/account/addresses/new",
    "/account/order-history": "/account/orders",
  };
  let target = exact[path];
  const match = path.match(/^\/account\/address\/([^/]+)\/edit$/);
  if (match) target = `/account/addresses/${encodeURIComponent(match[1])}/edit`;
  if (!target) return null;
  const full = String(route.fullPath || path);
  const query = full.includes("?")
    ? `?${full.split("?")[1].split("#")[0]}`
    : "";
  return `${target}${query}`;
}

export type AccountRoutePolicy = {
  account: boolean;
  guestOnly: boolean;
  requiresAuth: boolean;
};

type RouteLike = {
  path?: string;
  fullPath?: string;
  query?: Record<string, unknown>;
  meta?: Record<string, unknown>;
};

export function accountRoutePolicy(route: RouteLike): AccountRoutePolicy {
  const path = String(route.path || "");
  if (route.meta?.notFound === true) {
    return { account: false, guestOnly: false, requiresAuth: false };
  }
  const account =
    route.meta?.account === true || /^\/account(?:\/|$)/.test(path);
  const guestOnly = route.meta?.guestOnly === true || guestOnlyPaths.has(path);
  const requiresAuth =
    route.meta?.requiresAuth === true ||
    (account && !publicAccountPaths.has(path));
  return { account, guestOnly, requiresAuth };
}

export function safeGuestRedirect(value: unknown): string {
  const target = safeReturnTo(value, ACCOUNT_HOME_PATH);
  try {
    const path = new URL(target, "https://storefront.invalid").pathname;
    return guestOnlyPaths.has(path) ? ACCOUNT_HOME_PATH : target;
  } catch {
    return ACCOUNT_HOME_PATH;
  }
}

export type AccountGuardState = {
  authState: AuthState;
  sessionExpired?: boolean;
};

export function resolveAccountRoute(
  route: RouteLike,
  state: AccountGuardState
): true | { path: string; query?: Record<string, string> } | string {
  const policy = accountRoutePolicy(route);
  if (!policy.account) return true;

  if (policy.requiresAuth && state.authState === "anonymous") {
    const query: Record<string, string> = {
      returnTo: safeReturnTo(route.fullPath || route.path, ACCOUNT_HOME_PATH),
    };
    if (state.sessionExpired) query.reason = "expired";
    return { path: ACCOUNT_LOGIN_PATH, query };
  }

  if (policy.guestOnly && state.authState === "authenticated") {
    return safeGuestRedirect(route.query?.returnTo);
  }

  return true;
}

const installedRouters = new WeakSet<object>();

export async function enforceAccountRoute(route: RouteLike, store: any) {
  const legacy = legacyAccountRedirect(route);
  if (legacy) return legacy;
  const policy = accountRoutePolicy(route);
  if (!policy.account) return true;

  if (store.getters["common/customer/authState"] === "checking") {
    await store.dispatch("common/customer/checkLogged");
  }

  const sessionExpired =
    store.getters["common/customer/sessionExpired"] === true;
  const result = resolveAccountRoute(route, {
    authState: store.getters["common/customer/authState"],
    sessionExpired,
  });

  if (result !== true && sessionExpired) {
    store.commit("common/customer/acknowledgeSessionExpiry");
  }
  return result;
}

export function installAccountRouteGuards(router: any, store: any) {
  if (!router || installedRouters.has(router)) return;
  installedRouters.add(router);

  router.beforeEach(async (to: RouteLike, _from: RouteLike, next: any) => {
    const result = await enforceAccountRoute(to, store);
    next(result === true ? undefined : result);
  });
}

export type AccountHeaderEntry = {
  busy: boolean;
  label: string;
  to: string | null;
};

export function accountHeaderEntry(authState: AuthState): AccountHeaderEntry {
  if (authState === "checking") {
    return { busy: true, label: "Checking account status", to: null };
  }
  if (authState === "authenticated") {
    return { busy: false, label: "My account", to: ACCOUNT_HOME_PATH };
  }
  return { busy: false, label: "Log in", to: ACCOUNT_LOGIN_PATH };
}
